// src/layouts/MainLayout.js — V18 chrome.
//
// The header always overlays the page (every page starts with a WebGL strip
// or a full-bleed scene that leaves room for it). It turns solid once the
// inner scroller leaves the top, and on the home page it also retracts on
// scroll-down. A thin ceremonial frame with blood corner ticks sits over
// everything; the player is docked bottom-left.

import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../opencomponents/Header';
import MusicPlayer from '../opencomponents/MusicPlayer';
import { color } from '../theme/tokens';

const LayoutContainer = styled.div`
    position: relative;
    background-color: ${color.void};
    color: ${color.text};
    height: 100vh;
    height: 100dvh;
    width: 100vw;
    overflow: hidden;
`;

const Scroller = styled.main`
    position: absolute;
    inset: 0;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    & > * { width: 100%; flex-grow: 1; }
`;

const Frame = styled.div`
    position: fixed;
    inset: 16px;
    z-index: 95;
    pointer-events: none;
    border: 1px solid rgba(234, 225, 222, 0.1);
    &::before, &::after { content: ''; position: absolute; width: 16px; height: 16px; }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }
    @media (max-width: 720px) { inset: 8px; }
`;

const MainLayout = ({ navItems }) => {
    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [scrolled, setScrolled] = useState(false);
    const layoutRef = useRef(null);
    const lastTopRef = useRef(0);
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    // capture phase: scroll doesn't bubble, and the home page scrolls its own inner container
    useEffect(() => {
        const container = layoutRef.current;
        if (!container) return undefined;
        lastTopRef.current = 0;
        const onScroll = (e) => {
            const t = e.target;
            if (!t || typeof t.scrollTop !== 'number') return;
            const top = t.scrollTop;
            const delta = top - lastTopRef.current;
            lastTopRef.current = top;
            setScrolled(top > 40);
            if (!isHomePage) return;
            if (top <= 40) setIsHeaderVisible(true);
            else if (delta > 0) setIsHeaderVisible(false);
            else if (delta < 0) setIsHeaderVisible(true);
        };
        container.addEventListener('scroll', onScroll, true);
        return () => container.removeEventListener('scroll', onScroll, true);
    }, [isHomePage]);

    useEffect(() => {
        setIsHeaderVisible(true);
        setScrolled(false);
    }, [location.pathname]);

    return (
        <LayoutContainer ref={layoutRef}>
            <Header navItems={navItems} isVisible={isHeaderVisible} solid={scrolled && !isHomePage} />
            <Scroller key={location.pathname}>
                <Outlet />
            </Scroller>
            <Frame aria-hidden="true" />
            <MusicPlayer />
        </LayoutContainer>
    );
};

export default MainLayout;
